// Shared types + pure helpers for the immutable-ledger demo.
// No DB, no I/O — everything here is in-memory and deterministic.

export type Kind = 'revenue' | 'expense'

export interface JournalLine {
	id: string
	date: string // YYYY-MM-DD
	kind: Kind
	amount: number
}

export interface Version {
	label: string
	lines: JournalLine[]
}

export interface ChartSeries {
	key: string
	label: string
	color: string
	lines: JournalLine[]
}

/** Revenue is positive, expense negative. */
export function signed(line: JournalLine): number {
	const amt = Number.isFinite(line.amount) ? line.amount : 0
	return line.kind === 'revenue' ? amt : -amt
}

/** Net (sum of signed amounts) across every line — the book's bottom line. */
export function net(lines: JournalLine[]): number {
	return lines.reduce((sum, l) => sum + signed(l), 0)
}

/** Cumulative net across lines dated on or before `date`. */
export function cumulativeAt(lines: JournalLine[], date: string): number {
	return lines
		.filter((l) => l.date && l.date <= date)
		.reduce((sum, l) => sum + signed(l), 0)
}

/** Sorted unique set of dates appearing across all series. */
export function allDates(series: ChartSeries[]): string[] {
	const set = new Set<string>()
	for (const s of series) for (const l of s.lines) if (l.date) set.add(l.date)
	return [...set].sort()
}

/**
 * Merge N series into one dataset: one row per date, one numeric field per
 * series key, each value being that series' cumulative net up to and including
 * the row's date (carry-forward step lines).
 */
export function buildChartData(
	series: ChartSeries[],
): Array<Record<string, number | string>> {
	return allDates(series).map((date) => {
		const row: Record<string, number | string> = { date }
		for (const s of series) row[s.key] = cumulativeAt(s.lines, date)
		return row
	})
}

export const PALETTE = [
	'#6366f1', // indigo
	'#ec4899', // pink
	'#f59e0b', // amber
	'#10b981', // emerald
	'#3b82f6', // blue
	'#8b5cf6', // violet
	'#ef4444', // red
	'#14b8a6', // teal
	'#f97316', // orange
	'#a855f7', // purple
]

export function formatMoney(n: number): string {
	return n.toLocaleString('en-AU', {
		style: 'currency',
		currency: 'AUD',
		maximumFractionDigits: 0,
	})
}
